import React, { useRef, useState, useEffect } from "react";
import "./index.css";

export const DRAW_TYPE = {
  Free: 'pencil',
  Erase: 'eraser',
  Text: 'text',
};

const CustomCanvasV2 = ({ color, type, width, height, lineWidth, textSize, textColor, textWeight, textStyle, onClose, deleteAll, annotationInfo }) => {
  const parentRef = useRef(null);
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [paths, setPaths] = useState([]);
  const newPath = useRef({ color, lineWidth, path: [], textSize, textColor });
  const [isErasing, setIsErasing] = useState(false);
  const [ctx, setCtx] = useState();
  const [textInput, setTextInput] = useState("");
  const [textPosition, setTextPosition] = useState(null);
  const [isTextMode, setIsTextMode] = useState(false);
  const [isAnnotationMode, setIsAnnotationMode] = useState(false);
  // annotation 모드에서 시작점과 끝점을 저장하기 위한 상태
  const [annotationStart, setAnnotationStart] = useState(null);
  const [annotationEnd, setAnnotationEnd] = useState(null);

  // const [textColor, setTextColor] = useState(color);
  const inputRef = useRef(null);  // input 필드의 ref 추가

  const fontStyle = `${textStyle} ${textWeight} ${textSize}px sans-serif`; // 폰트 스타일 정의
  const fontAdjust = 0.36;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      context.strokeStyle = color;
      context.lineWidth = lineWidth;
      context.font = `${textStyle} ${textWeight} ${textSize}px sans-serif`; // 폰트 스타일 정의

      contextRef.current = context;
      setCtx(context);
    }
  }, []);

  useEffect(() => {
    setIsErasing(!!type.eraser);
    setIsTextMode(!!type.text);
    setIsAnnotationMode(!!type.annotation)

  }, [type]);

  useEffect(() => {
    handleResize();
  }, [width, height]);

  useEffect(() => {
    newPath.current.color = color;
  }, [color]);

  useEffect(() => {
    newPath.current.lineWidth = lineWidth;
  }, [lineWidth]);


  useEffect(() => {
    // 텍스트 입력 필드가 화면에 보일 때 자동으로 focus
    if (textPosition && inputRef.current) {
      inputRef.current.focus();
    }
  }, [textPosition]);

  useEffect(() => {
    if (deleteAll)
      handleTrash();
  }, [deleteAll])


  const handleTrash = () => {
    setPaths([]);
    redraw([]);
  }

  const handleResize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // 스케일링 전, 기존 캔버스 크기를 저장 (oldWidth, oldHeight)
    const oldWidth = canvas.width;
    const oldHeight = canvas.height;

    // 새 크기를 설정
    canvas.width = width;
    canvas.height = height;

    // 스케일링 함수 (좌표 하나를 변환)
    const scaleCoordinate = (x, y, fromW, fromH, toW, toH) => {
      return {
        x: (x / fromW) * toW,
        y: (y / fromH) * toH,
      };
    };

    // 도형별로 스케일링 처리
    const updatedPaths = paths.map(shape => {


      switch (shape.type) {
        // 1) 자유 그리기(기존 path 기반)
        case 'freeLine': {
          const scaledPath = shape.path.map(({ x, y }) =>
            scaleCoordinate(x, y, oldWidth, oldHeight, width, height)
          );
          return { ...shape, path: scaledPath };
        }

        case 'text': {
          // 텍스트의 위치를 path[0]에 저장한다고 가정
          if (shape.path && shape.path.length > 0) {
            const scaledPath = shape.path.map(({ x, y }) =>
              scaleCoordinate(x, y, oldWidth, oldHeight, width, height)
            );
            return { ...shape, path: scaledPath };
          }
          return shape;
        }

        case 'rectangle':
        case 'line': {
          // 사각형과 직선은 start, end를 사용
          // shape: { type, color, start: {x, y}, end: {x, y} }
          const scaledStart = scaleCoordinate(shape.start.x, shape.start.y, oldWidth, oldHeight, width, height);
          const scaledEnd = scaleCoordinate(shape.end.x, shape.end.y, oldWidth, oldHeight, width, height);
          return { ...shape, start: scaledStart, end: scaledEnd };
        }

        case 'circle': {
          // 원은 center, edge를 사용
          // shape: { type: 'circle', center: {x, y}, edge: {x, y} }
          const scaledCenter = scaleCoordinate(shape.center.x, shape.center.y, oldWidth, oldHeight, width, height);
          const scaledEdge = scaleCoordinate(shape.edge.x, shape.edge.y, oldWidth, oldHeight, width, height);
          return { ...shape, center: scaledCenter, edge: scaledEdge };
        }

        case 'point': {
          // 포인트는 position을 사용
          // shape: { type: 'point', position: { x, y }, color }
          const scaledPos = scaleCoordinate(shape.position.x, shape.position.y, oldWidth, oldHeight, width, height);
          return { ...shape, position: scaledPos };
        }

        default:
          return shape;
      }
    });

    // 스케일링된 paths로 업데이트하고 다시 그리기
    setPaths(updatedPaths);
    redraw(updatedPaths);
  };


  const startDrawing = ({ nativeEvent }) => {
    const { offsetX, offsetY } = nativeEvent;

    // console.log("startDrawing annotationInfo", isAnnotationMode, annotationInfo)
    if (isAnnotationMode && annotationInfo && annotationInfo.type) {
      if (annotationInfo.type === 'freeLine') {
        setIsDrawing(true);
        newPath.current = { type: 'freeLine', color: annotationInfo.color, lineWidth, path: [{ x: offsetX, y: offsetY }] };
      }
      else if (annotationInfo.type === 'point') {
        // point는 바로 그리기: 새로운 도형 객체를 paths에 추가
        const pointShape = {
          type: 'point',
          color: annotationInfo.color,
          position: { x: offsetX, y: offsetY },
        };
        setPaths(prev => [...prev, pointShape]);
        redraw([...paths, pointShape]);
      } else {
        // rectangle, line, circle: 시작 좌표 기록
        setAnnotationStart({ x: offsetX, y: offsetY });
        setAnnotationEnd(null);
      }
      return;
    }

    if (isErasing) {
      erasePath({ x: offsetX, y: offsetY });
    } else if (isTextMode) {
      // 텍스트 입력 모드일 때 클릭 시 위치를 설정
      if (textPosition && textInput) handleTextSubmit();
      setTextPosition({ x: offsetX, y: offsetY - textSize * 0.8 });
      setTextInput(""); // 텍스트 입력 초기화
      // inputRef.current?.focus();
      setTimeout(() => inputRef.current?.focus(), 0);

      // console.log(`🖱️ Canvas clicked at: x=${offsetX}, y=${offsetY}`);
    }
  };

  const finishDrawing = () => {
    if (annotationInfo && annotationInfo.type && annotationStart) {
      if (annotationInfo.type !== 'point' && annotationEnd) {
        let finalShape;
        if (annotationInfo.type === 'rectangle' || annotationInfo.type === 'line') {
          finalShape = {
            type: annotationInfo.type,
            color: annotationInfo.color,
            lineWidth: annotationInfo.lineWidth || lineWidth, // 선 굵기 추가
            start: annotationStart,
            end: annotationEnd,
          };
        } else if (annotationInfo.type === 'circle') {
          finalShape = {
            type: 'circle',
            color: annotationInfo.color,
            lineWidth: annotationInfo.lineWidth || lineWidth, // 선 굵기 추가
            center: annotationStart,
            edge: annotationEnd,
          };
        }
        setPaths(prev => [...prev, finalShape]);
        redraw([...paths, finalShape]);
      }
      setAnnotationStart(null);
      setAnnotationEnd(null);
      return;
    }

    // 기존 자유 그리기 모드 처리
    setIsDrawing(false);
    if (newPath.current.path.length > 0) {
      const updatedPaths = [...paths, { ...newPath.current }];
      setPaths(updatedPaths);
    }
    newPath.current = { color, lineWidth, path: [] };
  };

  const draw = ({ nativeEvent }) => {
    const { offsetX, offsetY } = nativeEvent;

    if (annotationInfo && annotationInfo.type && annotationStart) {
      setAnnotationEnd({ x: offsetX, y: offsetY });
      // 임시 미리보기 도형 생성
      let previewShape;
      if (annotationInfo.type === 'rectangle' || annotationInfo.type === 'line') {
        previewShape = {
          type: annotationInfo.type,
          color: annotationInfo.color,
          start: annotationStart,
          end: { x: offsetX, y: offsetY },
        };
      } else if (annotationInfo.type === 'circle') {
        previewShape = {
          type: 'circle',
          color: annotationInfo.color,
          center: annotationStart,
          edge: { x: offsetX, y: offsetY },
        };
      }
      // 임시 도형은 기존 paths와 합쳐서 그리기 (실제 paths에는 저장되지 않음)
      redraw([...paths, previewShape]);
      return;
    }

    if (!isDrawing || isErasing || isTextMode) return;
    newPath.current.path.push({ x: offsetX, y: offsetY });

    const updatedPaths = [...paths, { ...newPath.current }];
    redraw(updatedPaths);
  };
  const pointToLineDistance = (pt, lineStart, lineEnd) => {
    const { x, y } = pt;
    const { x: x1, y: y1 } = lineStart;
    const { x: x2, y: y2 } = lineEnd;
    const A = x - x1;
    const B = y - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    const dot = A * C + B * D;
    const len_sq = C * C + D * D;
    let param = (len_sq !== 0) ? (dot / len_sq) : -1;
    let xx, yy;
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }
    const dx = x - xx;
    const dy = y - yy;
    return Math.hypot(dx, dy);
  };


  // 선분과 점 사이의 최단 거리 계산 함수
  function pointToSegmentDistance(x, y, x1, y1, x2, y2) {
    const A = x - x1;
    const B = y - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const len_sq = C * C + D * D;
    let param = len_sq !== 0 ? dot / len_sq : -1;

    let nearestX, nearestY;
    if (param < 0) {
      nearestX = x1;
      nearestY = y1;
    } else if (param > 1) {
      nearestX = x2;
      nearestY = y2;
    } else {
      nearestX = x1 + param * C;
      nearestY = y1 + param * D;
    }

    return Math.hypot(x - nearestX, y - nearestY);
  }

  const erasePath = ({ x, y }) => {
    // paths: 기존에 그려진 도형들의 배열
    const clickedPathIndex = paths.findIndex((shape) => {
      const { lineWidth } = shape;

      switch (shape.type) {
        case 'text': {
          if (!shape.text || !shape.path || shape.path.length === 0) return false;
          const textWidth = contextRef.current.measureText(shape.text).width;
          const textHeight = shape.textSize;
          const textX = shape.path[0].x;
          const textY = shape.path[0].y;
          // console.debug(`Erasing text shape: text="${shape.text}", textX=${textX}, textY=${textY}, textWidth=${textWidth}, textHeight=${textHeight}, click=(${x}, ${y})`);
          // 텍스트 전체 영역 내에서 삭제할 수 있게 함 (원하는 경우 영역을 조정)
          return (x >= textX && x <= textX + textWidth &&
            y >= textY - textHeight && y <= textY + textHeight);
        }
        case 'rectangle': {
          const { start, end } = shape;
          const rectX = Math.min(start.x, end.x);
          const rectY = Math.min(start.y, end.y);
          const rectWidth = Math.abs(start.x - end.x);
          const rectHeight = Math.abs(start.y - end.y);
          const threshold = Math.max(5, lineWidth / 2);; // 경계와의 임계 거리(px)


          // 기존: 사각형 내부 체크 (변경 없음)
          if (x < rectX - threshold || x > rectX + rectWidth + threshold ||
            y < rectY - threshold || y > rectY + rectHeight + threshold) {
            return false;
          }

          // ✅ 테두리 고려: 선의 두께가 두꺼울 경우, 안쪽/바깥쪽 모두 감지하도록 수정
          const innerX = rectX + threshold;
          const innerY = rectY + threshold;
          const innerWidth = rectWidth - 2 * threshold;
          const innerHeight = rectHeight - 2 * threshold;

          const insideInnerBox = (x > innerX && x < innerX + innerWidth &&
            y > innerY && y < innerY + innerHeight);

          return !insideInnerBox; // 내부가 아닌 경계 부분을 클릭했을 때만 true 반환
        }
        case 'line': {
          const { start, end } = shape;
          return pointToLineDistance({ x, y }, start, end) < Math.max(10, lineWidth / 2);
        }
        case 'circle': {
          const { center, edge } = shape;
          const radius = Math.hypot(center.x - edge.x, center.y - edge.y);
          const distance = Math.hypot(x - center.x, y - center.y);
          const threshold = Math.max(5, lineWidth / 2); // 경계 임계치
          // 원의 경계 근처인지 (반지름과의 차이가 threshold 이하) 확인
          return Math.abs(distance - radius) <= threshold;
        }
        case 'point': {
          const { position } = shape;
          const w = (shape.lineWidth || lineWidth) * 3;   // 삼각형 가로폭
          const h = (shape.lineWidth || lineWidth) * 2;   // 삼각형 세로폭

          // 삼각형 영역 내부인지 확인
          // 삼각형 좌표: 우측(position.x, position.y), 위쪽(position.x-w, position.y-h), 아래쪽(position.x-w, position.y+h)
          const minX = position.x - w;
          const maxX = position.x;
          const minY = position.y - h;
          const maxY = position.y + h;

          // 먼저 bounding box 체크
          if (x < minX || x > maxX || y < minY || y > maxY) {
            return false;
          }

          // 삼각형 내부인지 더 정확히 체크
          const relativeX = x - position.x;  // position.x 기준 상대 좌표
          const relativeY = y - position.y;  // position.y 기준 상대 좌표

          // 삼각형의 좌측 부분 (화살표 몸체)에서의 클릭 감지
          if (relativeX >= -w && relativeX <= 0) {
            // 중간 지점(position.x - w * 0.65) 기준으로 위아래 범위 체크
            if (relativeX >= -w * 0.65) {
              // 화살표 끝부분 (좁은 부분)
              const allowedHeight = Math.abs(relativeY);
              const maxHeightAtX = h * (Math.abs(relativeX) / (w * 0.65));
              return allowedHeight <= maxHeightAtX;
            } else {
              // 화살표 몸체 부분 (넓은 부분)
              return Math.abs(relativeY) <= h;
            }
          }

          return false;
        }
        case 'freeLine': {
          const threshold = Math.max(10, lineWidth / 2);
          // 각 선분과 (x, y) 사이의 거리 확인
          for (let i = 0; i < shape.path.length - 1; i++) {
            const { x: x1, y: y1 } = shape.path[i];
            const { x: x2, y: y2 } = shape.path[i + 1];

            const distance = pointToSegmentDistance(x, y, x1, y1, x2, y2);
            if (distance < threshold) {
              return true;
            }
          }

          return false;
        }

        default:
          return false;
      }
    });

    if (clickedPathIndex !== -1) {
      const updatedPaths = [...paths];
      updatedPaths.splice(clickedPathIndex, 1);
      setPaths(updatedPaths);
      redraw(updatedPaths);
    }
  };

  // const redraw = (updatedPaths) => {
  //   const canvas = canvasRef.current;
  //   const context = canvas.getContext("2d");
  //   context.reset()
  //   context.clearRect(0, 0, canvas.width, canvas.height);

  //   // console.log("redraw", updatedPaths)

  //   updatedPaths.forEach(({ color, lineWidth, path, text, textSize, textColor, textWeight, textStyle }) => {
  //     context.strokeStyle = color;
  //     context.lineWidth = lineWidth;
  //     context.font = `${textStyle} ${textWeight} ${textSize}px sans-serif`; // 폰트 스타일 정의
  //     // console.log("redraw path", path, text)

  //     if (text) {
  //       context.fillStyle = textColor;
  //       context.font = `${textStyle} ${textWeight} ${textSize}px sans-serif`; // 폰트 스타일 정의
  //       context.textBaseline = "middle";  // 텍스트를 수직 중앙 정렬
  //       context.fillText(text, path[0].x + 1, path[0].y + textSize * 0.35); // 40% 보정
  //       context.stroke();

  //     } else if (path.length > 0) {
  //       context.beginPath();
  //       path.forEach(({ x, y }, index) => {
  //         if (index === 0) context.moveTo(x, y);
  //         else context.lineTo(x, y);
  //       });
  //       context.stroke();
  //     }
  //   });
  // };

  const redraw = (updatedPaths) => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    context.reset();
    context.clearRect(0, 0, canvas.width, canvas.height);

    // console.log("redraw", updatedPaths);

    updatedPaths.forEach(shape => {
      context.strokeStyle = shape.color;
      context.lineWidth = shape.lineWidth || lineWidth; // 기본값을 lineWidth로 설정

      if (shape.type === 'text') {
        const { textColor, textStyle, textWeight, textSize, path, text } = shape;

        context.fillStyle = textColor;
        context.font = `${textStyle} ${textWeight} ${textSize}px sans-serif`;
        context.textBaseline = "middle";
        context.fillText(text, path[0].x + 1, path[0].y + textSize * fontAdjust);
      } else if (shape.type === 'freeLine') {
        // 기존 자유 그리기 처리 (path 기반)
        context.strokeStyle = shape.color;
        context.lineWidth = shape.lineWidth || lineWidth;
        context.beginPath();
        shape.path.forEach(({ x, y }, index) => {
          if (index === 0) context.moveTo(x, y);
          else context.lineTo(x, y);
        });
        context.stroke();
      } else if (shape.type === 'rectangle') {
        // 사각형: 시작과 끝 좌표를 이용하여 rect 그리기
        const { start, end } = shape;
        context.strokeStyle = shape.color;
        context.lineWidth = shape.lineWidth || lineWidth;
        const rectX = Math.min(start.x, end.x);
        const rectY = Math.min(start.y, end.y);
        const rectWidth = Math.abs(start.x - end.x);
        const rectHeight = Math.abs(start.y - end.y);
        context.strokeRect(rectX, rectY, rectWidth, rectHeight);
      } else if (shape.type === 'line') {
        // 직선: 시작과 끝 좌표로 선 그리기
        const { start, end } = shape;
        context.strokeStyle = shape.color;
        context.lineWidth = shape.lineWidth || lineWidth;
        context.beginPath();
        context.moveTo(start.x, start.y);
        context.lineTo(end.x, end.y);
        context.stroke();
      } else if (shape.type === 'circle') {
        // 원: 시작 좌표를 중심, 끝 좌표와의 거리로 반지름 계산 후 원 그리기
        const { center, edge } = shape;
        context.strokeStyle = shape.color;
        context.lineWidth = shape.lineWidth || lineWidth;
        const radius = Math.hypot(center.x - edge.x, center.y - edge.y);
        context.beginPath();
        context.arc(center.x, center.y, radius, 0, 2 * Math.PI);
        context.stroke();
      } else if (shape.type === 'point') {
        // point: 화살표 모양 (오른쪽을 가리키는 작은 삼각형)
        const { position, color } = shape;
        const w = (shape.lineWidth || lineWidth) * 3;   // 삼각형 가로폭, 적당히 배수 조절
        const h = (shape.lineWidth || lineWidth) * 2;   // 삼각형 세로폭, 적당히 배수 조절

        context.fillStyle = color;
        context.beginPath();
        context.moveTo(position.x, position.y);                    // 삼각형 우측 꼭짓점 (클릭 지점)
        context.lineTo(position.x - w, position.y - h);            // 삼각형 위쪽 꼭짓점
        context.lineTo(position.x - w * 0.65, position.y);         // 삼각형 중간 지점
        context.lineTo(position.x - w, position.y + h);            // 삼각형 아래쪽 꼭짓점
        context.closePath();
        context.fill();
        if (!shape.lineWidth) {
          shape.lineWidth = lineWidth;
        }
      }
    });
  };


  const handleTextChange = (e) => {
    setTextInput(e.target.value);
    // console.log(`⌨️ Text Input Changed: ${e.target.value}`);
  };

  const handleInputFocus = (e) => {
    // 입력 필드가 포커스될 때 커서가 끝으로 가도록 설정
    const length = textInput.length;
    e.target.setSelectionRange(length, length);
  };


  const handleTextSubmit = () => {
    if (!textPosition || !textInput.trim()) return;

    const updatedPaths = [...paths, {
      type: 'text',
      textColor,
      textSize,
      textWeight,
      textStyle,
      path: [{ x: textPosition.x + 1, y: textPosition.y + textSize * fontAdjust }], // Y 위치 보정
      text: textInput,
    }];
    setTextPosition(null);
    setTextInput("");
    setPaths(updatedPaths);
    redraw(updatedPaths);
  };

  // const handleCanvasClick = ({ nativeEvent }) => {
  //   const { offsetX, offsetY } = nativeEvent;
  //   if (isTextMode && !textPosition) {
  //     // 텍스트 입력 모드일 때 클릭하여 입력 필드를 표시
  //     setTextPosition({ x: offsetX, y: offsetY + fontSize * 0.8 });
  //     setTextInput(""); // 텍스트 입력 초기화
  //     console.log(`🖱️ Canvas clicked at: x=${offsetX}, y=${offsetY}`);
  //   }
  // };

  return (
    <div
      className="canvas_wrap"
      ref={parentRef}
      width={width}
      height={height}
      style={{
        position: "relative",
        cursor: isTextMode
          ? "text"
          : isErasing
            ? "url('/assets/images/cursor/eraser.png') 15 30, auto"
            : "url('/assets/images/cursor/pencil.png') 5 30, auto",
      }}
    >
      <canvas
        ref={canvasRef}
        onMouseDown={startDrawing}
        onMouseUp={finishDrawing}
        onMouseMove={draw}
        onMouseLeave={finishDrawing}
      // onClick={handleCanvasClick}
      />
      {isTextMode && textPosition && (
        <input
          type="text"
          ref={inputRef} // input 필드의 ref 추가
          value={textInput}
          onChange={handleTextChange}
          onBlur={handleTextSubmit}
          onKeyDown={(e) => e.key === "Enter" && handleTextSubmit()}
          autoFocus
          color={textColor}
          onFocus={handleInputFocus}  // 커서 위치 끝으로 설정
          spellCheck={false}
          style={{
            position: "absolute",
            top: `${textPosition.y}px`,
            left: `${textPosition.x}px`,
            fontSize: `${textSize}px`, // 동일한 fontSize 적용
            fontFamily: "sans-serif",  // 동일한 fontFamily 적용
            fontWeight: textWeight,
            fontStyle: textStyle,
            color: textColor,
            border: "none",
            background: "transparent",
            outline: "none",
            width: `${textInput.length * textSize + 10}px`,
          }}
        />
      )}
    </div>
  );
};

export default CustomCanvasV2;
